<template>
  <div :class="{ 'has-no-before-integer-slot': hasNoBeforeIntegerSlot }" class="decimal-input">
    <div class="integer">
      <div class="input-group">
        <slot name="before-integer" class="input-group-addon" />
        <input
          v-bind="$attrs"
          ref="integerInput"
          :data-test="dataTestPrefix + 'integerInput'"
          :value="valueObject.integer"
          :readonly="readOnly"
          :min="minimumValueObject.integer"
          class="form-control text-right input-integer"
          :class="{ 'input-error': !!error }"
          type="number"
          step="1"
          name="amount_integer"
          @input="updateInteger"
          @blur="onBlurInteger"
        />
      </div>
    </div>
    <div class="decimal pull-left f-17 f-500 text-center p-t-5">
      {{ decimalPoint }}
    </div>
    <div :class="{ 'fraction-wide': fractionDecimals === 3 }" class="fraction">
      <input
        v-bind="$attrs"
        ref="fractionInput"
        :data-test="dataTestPrefix + 'fractionInput'"
        :value="valueObject.fraction"
        :required="required"
        :readonly="readOnly"
        :min="minimumValueObject.fraction"
        :max="fractionMax"
        :placeholder="fractionPlaceholder"
        class="form-control input-fraction"
        :class="{ 'input-error': !!error }"
        type="number"
        name="amount_fraction"
        @input="updateFraction"
        @blur="onBlurFraction"
        @focus="$event.target.select()"
      />
    </div>
    <input v-if="$attrs.name" :name="$attrs.name" :value="formattedValue" type="hidden" />
    <div class="clear" />
  </div>
  <div v-if="error && error.length" class="tw-text-red-500 tw-mt-1">
    <span>{{ error }}</span>
  </div>
</template>

<script>
/* eslint-disable no-param-reassign */
import { getRateAsIntegerAndFraction } from '@/components/utilities/numberFormatting'

export default {
  props: {
    modelValue: {
      type: Object,
      default: () => ({ integer: null, fraction: null }),
    },
    formatted: {
      type: Number,
      default: 0,
    },
    decimalPoint: {
      type: String,
      default: this?.account?.locale === 'da' ? ',' : '.',
    },
    required: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
      default: 0,
    },
    fractionMin: {
      type: Number,
      default: 0,
    },
    fractionMax: {
      type: Number,
      default: null,
    },
    fractionPlaceholder: {
      type: [Number, String],
      default: null,
    },
    error: {
      type: [Boolean, String],
      default: null,
    },
    fractionDecimals: {
      type: Number,
      default: 2,
      validator(value) {
        // We support double digit and triple digit fraction
        return [2, 3].indexOf(value) !== -1
      },
    },
    dataTestPrefix: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'update:formatted', 'blur'],
  computed: {
    valueObject() {
      return this.formatted ? this.convertToObject(this.formatted) : this.modelValue
    },
    formattedValue() {
      return parseFloat(this.convertToString(this.valueObject.integer, this.valueObject.fraction))
    },
    minimumValueObject() {
      const currentRateObject = getRateAsIntegerAndFraction(this.formattedValue)
      const minimumRateObject = getRateAsIntegerAndFraction(this.min)
      // if the main integer is lower than the minimum, we do not care about validating a minimum fraction
      // if the main integer is higher than the minimum, we do not care about validating a minimum fraction
      // if the main integer equals the minimum, we must validate a minimum fraction
      const minimumFraction =
        minimumRateObject.integer === currentRateObject.integer ? minimumRateObject.fraction : 0

      return { integer: minimumRateObject.integer, fraction: minimumFraction }
    },
    hasNoBeforeIntegerSlot() {
      return !this.$slots['before-integer']
    },
  },
  methods: {
    updateValueObject(valueObject) {
      this.$emit('update:modelValue', valueObject)
      this.$emit(
        'update:formatted',
        parseFloat(this.convertToString(valueObject.integer, valueObject.fraction)),
      )
    },
    updateInteger(event) {
      const { value } = event.target
      if (value) {
        const valueObject = {
          integer: +value,
          fraction: this.valueObject.fraction,
        }
        this.updateValueObject(valueObject)
      }
    },
    updateFraction(event) {
      const { value } = event.target
      if (value) {
        const valueObject = {
          integer: this.valueObject.integer,
          fraction: this.formatFraction(value),
        }
        this.updateValueObject(valueObject)
      }
    },
    onBlurInteger(event) {
      if (this.readOnly) {
        return
      }

      const { value } = event.target
      let integer = 0

      if (value) {
        integer = value
      }

      const valueObject = { integer, fraction: this.valueObject.fraction }
      this.updateValueObject(valueObject)
      event.target.value = integer
      this.$emit('blur')
    },
    onBlurFraction(event) {
      if (this.readOnly) {
        return
      }

      const { value } = event.target

      // default
      const zero = '0'
      let fraction = zero.repeat(this.fractionDecimals)

      if (value) {
        fraction = this.formatFractionOnBlur(value)
      }

      const valueObject = { integer: this.valueObject.integer, fraction }
      this.updateValueObject(valueObject)
      event.target.value = fraction
      this.$emit('blur')
    },
    formatFraction(value) {
      // if the value is more than allowed fraction decimals
      // format the number
      if (+value.length > this.fractionDecimals) {
        const val = value.slice(value.length - this.fractionDecimals)
        return val
      }

      return value
    },
    formatFractionOnBlur(val) {
      const prefix = '0'

      // handle zeros
      if (parseInt(val, 10) === 0) {
        return prefix.repeat(this.fractionDecimals)
      }

      // simply return the value if it "fits"
      if (val.length === this.fractionDecimals) {
        return val
      }

      const value = val

      // handle single digit fraction numbers
      if (+value.length <= 1) {
        return `${value}${prefix.repeat(this.fractionDecimals - 1)}`
      }

      // handle double digit fraction
      if (this.fractionDecimals >= 2 && +value.length <= 2) {
        return `${value}${prefix.repeat(this.fractionDecimals - 2)}`
      }

      // handle triple digit fraction
      if (this.fractionDecimals >= 3 && +value.length <= 3) {
        return `${value}${prefix.repeat(this.fractionDecimals - 3)}`
      }

      // if the value is longer than allowed
      if (+value.length > this.fractionDecimals) {
        return value.slice(value.length - this.fractionDecimals)
      }

      return parseInt(value.substring(0, this.fractionDecimals), 10)
    },
    focus() {
      this.$refs.integerInput.focus?.()
    },
    convertToString(integer, fraction) {
      return `${integer || 0}.${fraction || 0}`
    },
    convertToObject(value) {
      const parts = value.toString().split('.')
      return {
        integer: parseInt(parts[0], 10),
        fraction: this.formatFraction(parts[1] || '0'),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'resources/sass/frontend/_color-variables';

.decimal-input {
  display: flex;
  flex-wrap: wrap;
}

.input-integer {
  .has-no-before-integer-slot & {
    border-radius: 4px;
  }
}

.integer {
  flex: 1 0 0;
}

.fraction {
  flex: 0;
  flex-basis: 43px;
}

.fraction-wide {
  flex-basis: 48px;
}

.input-error:not(:focus) {
  border-color: $red-500 !important;
}
</style>
